import React from 'react'
import moment from 'moment-timezone'
import _ from 'lodash'
import PropTypes from 'prop-types'
import {
  ContentSection,
  ContentSectionBlock,
  Row,
  Col,
  Table
} from 'camino-react'

import { cloudhelpGet, cloudhelpDelete } from 'Utilities/CloudHelp'
import TimesheetModal from 'Components/Timesheets/TimesheetModal'

const NewProblemTimesheetTrigger = props => (
  <button {...props} type="button" className="form-section__action button button--wide">
    Add Timesheet
  </button>
)

const ActionsCell = ({
  row,
  setTimesheet,
  destroyTimesheet,
  policies,
  currentUser
}) => {
  const { path } = row.original
  return (
    <Row noGutters>
      { policies.problem.timesheet.update && currentUser.id === row.original.user_id && (
        <Col>
          <button
            style={{ border: 'none', background: 'transparent', outline: 'none' }}
            type="button"
            className="lnr lnr-pencil lnr--hover-blue"
            onClick={() => { setTimesheet('editable', row.original) }}
          />
        </Col>
      )}
      { policies.problem.timesheet.destroy && currentUser.id === row.original.user_id && (
        <Col>
          <button
            style={{ border: 'none', background: 'transparent', outline: 'none' }}
            type="button"
            className="lnr lnr-trash2 lnr--hover-blue"
            onClick={() => {
              if (confirm('Are you sure you want to delete this timesheet?')) {
                destroyTimesheet(path)
              }
            }}
          />
        </Col>
      )}
    </Row>
  )
}

class AssociatedTimesheets extends React.Component {
  constructor (props) {
    super(props)

    this.state = {
      timesheets: [],
      editableTimesheet: null,
      viewableTimesheet: null,
      loading: false,
      users: {},
      modalOpen: false
    }
  }

  componentDidMount () {
    const { paths } = this.props

    this.gatherTimesheets()

    // loop through the  'itemsToRequest' and makes a request
    // to its index page(gathering all records)
    _.map(paths.workRolesAndTypes, item => this.requestAndSetItems(item))
  }

  setTimesheet = (type, timesheet) => {
    this.setState({ [`${type}Timesheet`]: timesheet })
  }

  requestAndSetItems = (type) => {
    cloudhelpGet(type.path)
      .then((response) => {
        this.setState({
          [type.name]: response.data
        })
      })
  }

  decorateTimesheet = (timesheet) => {
    const {
      users,
      workRoles,
      workTypes
    } = this.state

    const workRole = timesheet.work_role_name || 'N/A'
    const workType = timesheet.work_type_name || 'N/A'

    return ({
      ...timesheet,
      workRole: workRole,
      workType: workType,
      userName: timesheet.user_name ? timesheet.user_name : 'N/A',
      description: timesheet.description
    })
  }

  gatherTimesheets = () => {
    const { paths: { apiProblemTimesheetsPath } } = this.props

    this.setState({ loading: true })

    return cloudhelpGet(apiProblemTimesheetsPath)
      .then((response) => {
        const timesheets = response.data.problem
        this.setState(
          {
            timesheets,
            loading: false,
            modalOpen: true
          },
          () => this.gatherTimesheetUsers(timesheets)
        )
      })
  }

  gatherTimesheetUsers = (timesheets) => {
    const uniqueTimesheetUserIds = _(timesheets).map('user_id').uniq().values()

    return cloudhelpGet('/api/users', { user_ids: uniqueTimesheetUserIds })
      .then((response) => {
        const users = _.map(
          response.data,
          user => ({ id: user.id, name: user.name })
        )

        this.setState({ users })
      })
  }

  destroyTimesheet = (timesheetPath) => {
    const { setAlert } = this.props

    cloudhelpDelete(timesheetPath)
      .then(() => {
        this.gatherTimesheets()
          .then(() => {
            setAlert(
              'Timesheet deleted successfully',
              'success',
              true
            )
          })
      })
      .catch((error) => {
        setAlert(
          `Timesheet failed to remove due to the following errors: ${error.response.data}`,
          'error',
          true
        )
      })
  }

  closeModal () {
    this.setState({ modalOpen: false })
  }

  formatStringFromObject (object) {
    return JSON.stringify(object).replace(/\\n/g, ' ').slice(1, -1)
  }

  render () {
    const {
      setTimesheet,
      destroyTimesheet,
      decorateTimesheet,
      formatStringFromObject
    } = this

    const {
      loading,
      timesheets,
      editableTimesheet,
      viewableTimesheet,
      modalOpen
    } = this.state

    const {
      hidden,
      policies,
      paths,
      currentUser
    } = this.props

    const { apiProblemTimesheetsPath } = paths

    const canCreateTimesheets = policies.problem.timesheet.create

    if (hidden) {
      return null
    }

    const data = timesheets && (_.map(timesheets, timesheet => decorateTimesheet(timesheet)))

    const columns = [
      { text: 'Start', accessor: 'start_time', Cell: row => (moment(row.value).format('DD MMM YYYY, hh:mmA')) },
      { text: 'End', accessor: 'end_time', Cell: row => (moment(row.value).format('DD MMM YYYY, hh:mmA')) },
      { text: 'User name', accessor: 'userName' },
      {
        text: 'Chargeable?',
        accessor: 'chargeable',
        maxWidth: 50,
        Cell: row => (row.value ? 'Yes' : 'No')
      },
      {
        text: 'Description',
        accessor: 'description',
        minWidth: 250,
        Cell: row => (
          <span
            style={{ cursor: 'pointer', color: '#00baea' }}
            onClick={() => { setTimesheet('viewable', row.original) }}
          >
            {formatStringFromObject(row.original.description)}
          </span>
        )
      },
      { text: 'Note', accessor: 'comments' },
      { text: 'Work Type', accessor: 'workType' },
      { text: 'Work Role', accessor: 'workRole' }
    ]

    if (canCreateTimesheets) {
      columns.push(
        {
          text: 'Actions',
          accessor: '',
          Cell: row => (
            <ActionsCell
              row={row}
              setTimesheet={setTimesheet}
              destroyTimesheet={destroyTimesheet}
              policies={policies}
              currentUser={currentUser}
            />
          )
        }
      )
    }

    return (
      <div>
        <ContentSection card>
          <ContentSectionBlock title="Associated timesheets">

            { _.some(data) &&
              (
                <Table
                  data={data}
                  columns={columns}
                  loading={loading}
                  noDataText="There are not currently any timesheets logged for this problem."
                  localStoragePrefix="AssociatedTimesheets"
                />
              )
            }

            { _.isEmpty(timesheets) && (
              <h4>
                There are no timesheets logged for this problem
              </h4>
            )}

            { (modalOpen && canCreateTimesheets) && (
              <React.Fragment>
                <TimesheetModal
                  title="New Timesheet"
                  url={apiProblemTimesheetsPath}
                  method="post"
                  key="create"
                  trigger={(
                    <button type="button" className="form-section__action button button--wide">
                        Add timesheet
                    </button>
                  )}
                  onClose={() => {
                    this.closeModal()
                    this.gatherTimesheets()
                  }}
                  modalOpen
                  newTimesheet
                />
                { (editableTimesheet || viewableTimesheet) && (
                  <TimesheetModal
                    open
                    editableTimesheet={!!editableTimesheet}
                    viewableTimesheet={!!viewableTimesheet}
                    key="edit"
                    method={editableTimesheet ? 'patch' : null}
                    url={editableTimesheet ? editableTimesheet.path : null}
                    title={editableTimesheet ? 'Edit Timesheet' : 'Problem Timesheet Summary'}
                    timesheet={editableTimesheet || viewableTimesheet}
                    onClose={() => {
                      editableTimesheet ? this.setTimesheet('editable', null) : this.setTimesheet('viewable', null)
                      this.gatherTimesheets()
                    }}
                    modalOpen
                  />
                )}
              </React.Fragment>
            )}
          </ContentSectionBlock>
        </ContentSection>
      </div>
    )
  }
}

AssociatedTimesheets.propTypes = {
  policy: PropTypes.func.isRequired,
  paths: PropTypes.shape({
    apiProblemTimesheetsPath: PropTypes.string.isRequired
  }).isRequired
}

export default AssociatedTimesheets
